import { useEffect, useRef } from 'react';
import axios from 'axios';

const PROXY_URL = '';

/**
 * Fetches 1h of Birdeye historical price data on mount for each supported coin.
 * Seeds the priceHistory state so graphs have data immediately.
 * Also seeds settledHistory with the last 3 completed minute candles.
 *
 * Only BTC, ETH, SOL are supported via Birdeye.
 *
 * @param {Function} setPriceHistory - State setter from App.js
 * @param {Function} setSettledHistory - State setter for settled pane from App.js
 */
const BIRDEYE_COINS = ['bitcoin', 'ethereum', 'solana'];

const useBirdeyeHistory = (setPriceHistory, setSettledHistory) => {
    const hasFetched = useRef(false);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const fetchHistory = async (coinId) => {
            try {
                const response = await axios.get(
                    `${PROXY_URL}/api/history/${coinId}?hours=1`,
                    { timeout: 10000 }
                );

                const items = response.data?.items;
                if (!items || items.length === 0) return;

                // Transform to the format usePrices uses: { time, price }
                const historyPoints = items.map(item => ({
                    time: item.time,
                    price: item.price
                }));

                setPriceHistory(prev => ({
                    ...prev,
                    [coinId]: [
                        ...historyPoints,
                        ...(prev[coinId] || [])
                    ].slice(-720) // Keep max 12h of points
                }));

                // Seed settled history from minute candles
                if (setSettledHistory && items.length >= 4) {
                    // Build minute candles grouped by minute boundary
                    const now = new Date();
                    const currentMinuteMs = new Date(
                        now.getFullYear(), now.getMonth(), now.getDate(),
                        now.getHours(), now.getMinutes(), 0, 0
                    ).getTime();

                    const minuteMap = {};
                    items.forEach(item => {
                        const minuteMs = Math.floor(item.time / 60000) * 60000;
                        if (!minuteMap[minuteMs]) {
                            minuteMap[minuteMs] = { open: item.price, close: item.price, time: minuteMs };
                        }
                        minuteMap[minuteMs].close = item.price;
                    });

                    // Sort descending, exclude current (incomplete) minute, take last 3
                    const candles = Object.values(minuteMap)
                        .filter(c => c.time < currentMinuteMs)
                        .sort((a, b) => b.time - a.time)
                        .slice(0, 3);

                    if (candles.length > 0) {
                        const settled = candles.map(c => {
                            const d = new Date(c.time);
                            return {
                                time: `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}`,
                                start: c.open,
                                end: c.close,
                                slotTimeMs: c.time,
                            };
                        });

                        setSettledHistory(prev => ({
                            ...prev,
                            [coinId]: settled,
                        }));
                    }
                }

                console.log(
                    `[Birdeye] Seeded ${coinId} graph with ${historyPoints.length} points`
                );
            } catch (error) {
                // Silently fail — Binance live data will fill the graph
                console.warn(
                    `[Birdeye] Could not seed ${coinId}: ${error.message}`
                );
            }
        };

        // Fetch all supported coins in parallel
        Promise.all(BIRDEYE_COINS.map(fetchHistory));
    }, [setPriceHistory, setSettledHistory]);
};

export default useBirdeyeHistory;
